import React from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { TabContext, TabList, TabPanel } from '@material-ui/lab';
import { Grid, Tab, AppBar, Hidden, Link } from '@material-ui/core';
import i18n from '../../../i18n';

import PaymentsHeader from 'assets/images/payments_header.png';
import Payments_0 from 'assets/images/payments.png';
import Payments_1 from 'assets/images/payments_1.png';
import Payments_2 from 'assets/images/payments_2.png';
import Payments_3 from 'assets/images/payments_3.png';
import LogoKassa from 'assets/images/io_kassa.svg';
import { ReactComponent as YoutubeIcon } from 'assets/icons/main-24/youtube--24.svg';

import { Text, TextTypography } from 'components/Text/Text';
import { Header } from 'components/Header/Header';
import { FooterInternalPages } from 'components/Footer/FooterInternalPages';
import { Icon } from 'components/Icon/Icon';
import useScrollPosition from '@react-hook/window-scroll';
import useVideoLink from '../../core/hooks/useVideoLink';

const useStyles = makeStyles(theme => ({
    mainContainer: {
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
    },
    headerContainer: {
        position: 'fixed',
        width: '100%',
        top: 0,
        left: 0,
        zIndex: 1,
        backgroundColor: props => (props.scrollY > 664 ? '#FBFEF9' : null),
    },
    tagline: {
        height: 664,
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        background: `url(${PaymentsHeader}) no-repeat`,
        backgroundSize: 'cover',
        backgroundPosition: '60%',
    },
    taglineLink: {
        display: 'flex',
        alignContent: 'center',
        marginTop: theme.spacing(5),
        marginLeft: theme.spacing(17.75),
        height: 24,
        [theme.breakpoints.down(1280)]: {
            marginLeft: theme.spacing(5),
        },
        [theme.breakpoints.down(768)]: {
            marginLeft: theme.spacing(4),
        },
        [theme.breakpoints.down(480)]: {
            marginLeft: theme.spacing(2),
        },
    },
    taglineLinkText: {
        marginLeft: theme.spacing(1),
        color: '#FFFFFF',
    },
    taglineText: {
        maxWidth: 537,
        marginLeft: theme.spacing(17.75),
        color: '#FFFFFF',
        textShadow: '0px 0px 60px rgba(0, 0, 0, 0.8)',
        [theme.breakpoints.down(1280)]: {
            marginLeft: theme.spacing(5),
        },
        [theme.breakpoints.down(768)]: {
            marginLeft: theme.spacing(4),
        },
        [theme.breakpoints.down(480)]: {
            marginLeft: theme.spacing(2),
        },
    },
    additionalContainer: {
        width: '100%',
        padding: theme.spacing(10, 17.75),
        margin: '0 auto',
        textAlign: 'left',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        [theme.breakpoints.down(1280)]: {
            padding: theme.spacing(10, 5),
        },
        [theme.breakpoints.down(960)]: {
            padding: theme.spacing(8, 5),
        },
        [theme.breakpoints.down(768)]: {
            padding: theme.spacing(6, 4),
        },
        [theme.breakpoints.down(480)]: {
            padding: theme.spacing(6, 2),
        },
    },
    additionalTitle: {
        width: '100%',
        marginTop: theme.spacing(7),
        '&:first-child': {
            marginTop: 0,
        },
        [theme.breakpoints.down(960)]: {
            marginTop: theme.spacing(8),
        },
        [theme.breakpoints.down(768)]: {
            marginTop: theme.spacing(6),
        },
        [theme.breakpoints.down(640)]: {
            marginTop: theme.spacing(5),
        },
    },
    additionalTitleText: {
        letterSpacing: -1,
    },
    additionalSubTitle: {
        width: 'inherit',
        marginTop: theme.spacing(3),
    },
    additionalGrid: {
        width: '46%',
        marginBottom: 8,
        marginRight: 32,
        '&:last-child': {
            marginRight: 0,
        },
    },
    additionalGridText: {
        width: '47%',
        marginTop: 56,
    },
    grid: {
        width: '47%',
        margin: theme.spacing(6, 0),
        marginRight: 32,
        overflow: 'hidden',
        border: '1px solid rgba(36, 49, 67, 0.2)',
        boxSizing: 'border-box',
        boxShadow: '0px 15px 32px -3px rgba(36, 49, 67, 0.2)',
        borderRadius: 8,
        wrap: 'no-wrap',
        [theme.breakpoints.down(665)]: {
            marginRight: 16,
        },
        [theme.breakpoints.down(400)]: {
            width: '47%',
            marginTop: 16,
            '&:first-child': {
                maxWidth: 288,
                width: '100%',
                marginTop: 48,
                marginRight: 0,
            },
        },
        '&:last-child': {
            marginRight: 0,
        },
    },
    gridSmall: {
        display: 'flex',
        justifyContent: 'space-around',
        width: '100%',
        height: 184,
        margin: theme.spacing(6, 0),
        overflow: 'hidden',
        border: '1px solid rgba(36, 49, 67, 0.2)',
        boxSizing: 'border-box',
        borderRadius: 8,
        background: '#213F85',
        [theme.breakpoints.down(1280)]: {
            height: 163,
        },
        [theme.breakpoints.down(960)]: {
            margin: theme.spacing(4, 0),
            height: 128,
        },
        [theme.breakpoints.down(768)]: {
            height: 110,
        },
        [theme.breakpoints.down(640)]: {
            height: 80,
        },
        [theme.breakpoints.down(480)]: {
            height: 61,
        },
    },
    gridMiddle: {
        width: '100%',
        maxHeight: 276,
        marginTop: 32,
        border: '1px solid rgba(36, 49, 67, 0.2)',
        boxShadow: '0px 15px 32px -3px rgba(36, 49, 67, 0.2)',
        boxSizing: 'border-box',
        borderRadius: 8,
        [theme.breakpoints.down(321)]: {
            maxHeight: 184,
        },
    },
    gridBig: {
        width: '100%',
        margin: theme.spacing(6, 0),
        overflow: 'hidden',
        boxSizing: 'border-box',
        borderRadius: theme.spacing(1),
        [theme.breakpoints.down(960)]: {
            margin: theme.spacing(4, 0),
        },
    },
    additionalImg: {
        objectFit: 'fill',
        width: '100%',
        height: '100%',
        borderRadius: theme.spacing(1),
    },
    additionalLogo: {
        height: 95,
        margin: 'auto',
        [theme.breakpoints.down(1280)]: {
            maxHeight: 84,
        },
        [theme.breakpoints.down(960)]: {
            maxHeight: 66,
        },
        [theme.breakpoints.down(768)]: {
            maxHeight: 56,
        },
        [theme.breakpoints.down(640)]: {
            maxHeight: 41,
        },
        [theme.breakpoints.down(480)]: {
            maxHeight: 31,
        },
    },
    footerContainer: {
        width: '100%',
        margin: '0 auto',
    },
    appBarContainer: {
        marginTop: 52,
    },
    appBar: {
        borderRadius: 12,
        backgroundColor: 'rgba(15, 24, 37, 0.1)',
    },
    appBarTab: {
        width: '50%',
        margin: '0 auto',
    },
    tabPanel: {
        paddingLeft: 0,
        paddingRight: 0,
    },
    link: {
        maxHeight: 24,
        display: 'flex',
        alignItem: 'center',
        textDecoration: 'none',
    },
}));

export const Payments = () => {
    const scrollY = useScrollPosition(60 /*fps*/);
    const classes = useStyles({ scrollY });
    const { t } = i18n.useTranslation();

    const [value, setValue] = React.useState('1');
    const videoLink = useVideoLink();

    const handleChange = (event: any, newValue: any) => {
        setValue(newValue);
    };

    return (
        <div className={classes.mainContainer}>
            <div className={classes.headerContainer}>
                <Header />
            </div>

            <div className={classes.tagline}>
                <Text className={classes.taglineText} typography={TextTypography.TITLE_STRONG_CHANGE}>
                    {t('pages:payments.title')}
                </Text>
                <div className={classes.taglineLink}>
                    <Icon component={YoutubeIcon} />
                    <Link className={classes.link} href={videoLink} target="_blank" rel="noreferrer" color="inherit">
                        <Text className={classes.taglineLinkText} typography={TextTypography.TEXT_ACCENT_MEDIUM_CHANGE}>
                            {t('pages:payments.subtitle')}
                        </Text>
                    </Link>
                </div>
            </div>
            <div className={classes.additionalContainer}>
                <div className={classes.additionalTitle}>
                    <Text className={classes.additionalTitleText} typography={TextTypography.HUGE_TITLE_BOLD_CHANGE}>
                        {t('pages:payments.block1.title')}
                    </Text>
                </div>
                <div className={classes.additionalSubTitle}>
                    <Text className={classes.additionalTitleText} typography={TextTypography.TITLE_ACCENT_CHANGE}>
                        {t('pages:payments.block1.subTitle')}
                    </Text>
                </div>
                <Grid container>
                    <Grid item xs className={classes.gridBig}>
                        <img className={classes.additionalImg} src={Payments_0} alt={t('common:photoAltShop')} />
                    </Grid>
                </Grid>
                <div>
                    <Text typography={TextTypography.TEXT_BODY_REGULAR_CHANGE}>{t('pages:payments.block1.text1')}</Text>
                </div>
                <div className={classes.additionalSubTitle}>
                    <Text typography={TextTypography.TEXT_ACCENT_MEDIUM_CHANGE}>
                        {t('pages:payments.block1.text2')}
                    </Text>
                </div>
                <div className={classes.additionalSubTitle}>
                    <Text typography={TextTypography.TEXT_BODY_REGULAR_CHANGE}>{t('pages:payments.block1.text3')}</Text>
                </div>
                <Grid container>
                    <Grid item xs className={classes.gridSmall}>
                        <img className={classes.additionalLogo} src={LogoKassa} alt="логотип кассы" />
                    </Grid>
                </Grid>
                <div>
                    <Text typography={TextTypography.TEXT_BODY_REGULAR_CHANGE}>{t('pages:payments.block2.text1')}</Text>
                </div>
                <div className={classes.additionalTitle}>
                    <Text typography={TextTypography.TITLE_ACCENT_CHANGE}>{t('pages:payments.block7.title')}</Text>
                </div>
                <div className={classes.additionalSubTitle}>
                    <Text typography={TextTypography.TEXT_BODY_REGULAR_CHANGE}>{t('pages:payments.block7.text')}</Text>
                </div>
                <div className={classes.additionalSubTitle}>
                    <Text typography={TextTypography.TEXT_BODY_REGULAR_CHANGE}>{t('pages:payments.block7.text1')}</Text>
                </div>
                <div className={classes.additionalSubTitle}>
                    <Text typography={TextTypography.TEXT_BODY_REGULAR_CHANGE}>{t('pages:payments.block7.text2')}</Text>
                </div>
                <div className={classes.additionalSubTitle}>
                    <Text typography={TextTypography.TEXT_BODY_REGULAR_CHANGE}>{t('pages:payments.block7.text3')}</Text>
                </div>
                <div className={classes.additionalSubTitle}>
                    <Text typography={TextTypography.TEXT_BODY_REGULAR_CHANGE}>{t('pages:payments.block2.text2')}</Text>
                </div>
                <Hidden xsDown>
                    <Grid container justify="center">
                        <Grid item xs className={classes.grid}>
                            <img
                                className={classes.additionalImg}
                                src={Payments_1}
                                alt={t('common:photoAltInstruction')}
                            />
                        </Grid>

                        <Grid item xs className={classes.grid}>
                            <img
                                className={classes.additionalImg}
                                src={Payments_2}
                                alt={t('common:photoAltInstruction')}
                            />
                        </Grid>
                    </Grid>
                    <Grid container justify="center">
                        <div className={classes.additionalGrid}>
                            <Text typography={TextTypography.TEXT_BODY_REGULAR_CHANGE}>
                                {t('pages:payments.block3.text1')}
                            </Text>
                        </div>
                        <div className={classes.additionalGrid}>
                            <Text typography={TextTypography.TEXT_BODY_REGULAR_CHANGE}>
                                {t('pages:payments.block3.text2')}
                            </Text>
                        </div>
                    </Grid>
                    <Grid container justify="center">
                        <Grid item className={classes.grid}>
                            <img
                                className={classes.additionalImg}
                                src={Payments_3}
                                alt={t('common:photoAltInstruction')}
                            />
                        </Grid>
                        <div className={classes.additionalGridText}>
                            <Text typography={TextTypography.TEXT_BODY_REGULAR_CHANGE}>
                                {t('pages:payments.block3.text3')}
                            </Text>
                        </div>
                    </Grid>
                </Hidden>
                <Hidden smUp>
                    <Grid container>
                        <Grid item xs className={classes.gridMiddle}>
                            <img
                                className={classes.additionalImg}
                                src={Payments_1}
                                alt={t('common:photoAltInstruction')}
                            />
                        </Grid>
                    </Grid>
                    <div className={classes.additionalSubTitle}>
                        <Text typography={TextTypography.TEXT_BODY_REGULAR_CHANGE}>
                            {t('pages:payments.block3.text1')}
                        </Text>
                    </div>
                    <Grid container>
                        <Grid item xs className={classes.gridMiddle}>
                            <img
                                className={classes.additionalImg}
                                src={Payments_2}
                                alt={t('common:photoAltInstruction')}
                            />
                        </Grid>
                    </Grid>
                    <div className={classes.additionalSubTitle}>
                        <Text typography={TextTypography.TEXT_BODY_REGULAR_CHANGE}>
                            {t('pages:payments.block3.text2')}
                        </Text>
                    </div>
                    <Grid container>
                        <Grid item xs className={classes.gridMiddle}>
                            <img
                                className={classes.additionalImg}
                                src={Payments_3}
                                alt={t('common:photoAltInstruction')}
                            />
                        </Grid>
                    </Grid>
                    <div className={classes.additionalSubTitle}>
                        <Text typography={TextTypography.TEXT_BODY_REGULAR_CHANGE}>
                            {t('pages:payments.block3.text3')}
                        </Text>
                    </div>
                </Hidden>
                <div>
                    <Text typography={TextTypography.TEXT_BODY_REGULAR_CHANGE}>{t('pages:payments.block4.text1')}</Text>
                </div>
                <div className={classes.appBarContainer}>
                    <TabContext value={value}>
                        <AppBar className={classes.appBar} position="static">
                            <TabList onChange={handleChange} textColor="primary" centered>
                                <Tab className={classes.appBarTab} label={t('pages:payments.block4.text2')} value="1" />
                                <Tab className={classes.appBarTab} label={t('pages:payments.block4.text3')} value="2" />
                            </TabList>
                        </AppBar>
                        <TabPanel className={classes.tabPanel} value="1">
                            <div className={classes.additionalSubTitle}>
                                <Text typography={TextTypography.TEXT_BODY_REGULAR_CHANGE}>
                                    {t('pages:payments.block5.text1')}
                                </Text>
                            </div>
                            <div className={classes.additionalSubTitle}>
                                <Text typography={TextTypography.TEXT_BODY_REGULAR_CHANGE}>
                                    {t('pages:payments.block5.text2')}
                                </Text>
                            </div>
                            <div className={classes.additionalSubTitle}>
                                <Text typography={TextTypography.TEXT_BODY_REGULAR_CHANGE}>
                                    {t('pages:payments.block5.text3')}
                                </Text>
                            </div>
                            <div className={classes.additionalSubTitle}>
                                <Text typography={TextTypography.TEXT_BODY_REGULAR_CHANGE}>
                                    {t('pages:payments.block5.text4')}
                                </Text>
                            </div>
                        </TabPanel>
                        <TabPanel className={classes.tabPanel} value="2">
                            <div className={classes.additionalSubTitle}>
                                <Text typography={TextTypography.TEXT_BODY_REGULAR_CHANGE}>
                                    {t('pages:payments.block6.text1')}
                                </Text>
                            </div>
                            <div className={classes.additionalSubTitle}>
                                <Text typography={TextTypography.TEXT_BODY_REGULAR_CHANGE}>
                                    {t('pages:payments.block6.text2')}
                                </Text>
                            </div>
                            <div className={classes.additionalSubTitle}>
                                <Text typography={TextTypography.TEXT_BODY_REGULAR_CHANGE}>
                                    {t('pages:payments.block6.text3')}
                                </Text>
                            </div>
                            <div className={classes.additionalSubTitle}>
                                <Text typography={TextTypography.TEXT_BODY_REGULAR_CHANGE}>
                                    {t('pages:payments.block6.text4')}
                                </Text>
                            </div>
                        </TabPanel>
                    </TabContext>
                </div>
            </div>
            <div className={classes.footerContainer}>
                <FooterInternalPages />
            </div>
        </div>
    );
};
